import ExportarCsv from '@/global/components/DataGrid/components/CustomGridToolBar/ExportarCsv'
import { GridMenuButton } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { useFilterLocaisCentroStore } from '@/global/components/Selects'
import { useUrlState } from '@/hooks'
import localeText from '@/locales/datagrid'
import { CalculoEspelho, espelhoPonto } from '@/service/api'
import { PaginatedResponse } from '@/service/api/types'
import { useStore } from '@/store'
import BaixarCalculoButton from '@/templates/ManutencaoDoPonto/CalculoPonto/BaixarCalculoButton'
import CalcularEmpresa from '@/templates/ManutencaoDoPonto/CalculoPonto/calcular'
import columns from '@/templates/ManutencaoDoPonto/CalculoPonto/columns'
import SelectCompetencia, {
    useCompetencia,
} from 'global/components/Selects/SelectCompetencia'
import ContentGuide from 'global/guides/ContentGuide'
import { UploadIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import OverlayApresentacao, {
    IPasso,
} from 'templates/Apresentacao/OverlayApresentacao'

const isMobilePresentation = window.innerWidth <= 1024 // lg

const passos: IPasso[] = [
    {
        title: 'Calcule o ponto',
        description: `Voce pode calcular o ponto de todos os funcionários da empresa
        de uma só vez clicando no botão "Calcular".`,
        getHighlightElement: () =>
            document.querySelector('[data-testid="calcular-empresa"]')!,
        parentElements: isMobilePresentation
            ? [
                  () =>
                      document.querySelector(
                          '[data-testid="botao_opcoes_datatable"]',
                      )!,
              ]
            : undefined,
    },
    {
        title: 'Exportação',
        description: `Exporte os PDFs dos funcionários em um único arquivo ou ZIP. Selecione os funcionários na tabela e clique em "Exportar".`,
        parentElements: isMobilePresentation
            ? [
                  () =>
                      document.querySelector(
                          '[data-testid="botao_opcoes_datatable"]',
                      )!,
                  () =>
                      document.querySelector(
                          '[data-testid="botao_exportar-calculo"]',
                      )!,
              ]
            : [
                  () =>
                      document.querySelector(
                          '[data-testid="botao_exportar-calculo"]',
                      )!,
              ],
        getHighlightElement: () =>
            document.getElementsByClassName('MuiList-root')[0] as any,
    },
]

export default function CalculoPonto() {
    const [competencia, setCompetencia] = useCompetencia()
    const empresaId = useStore(state => state.companyId)
    const [selectedIds, setSelectedIds] = useState<number[]>([])

    useEffect(() => {
        setSelectedIds([])
    }, [empresaId])

    const [{ page, perPage }, setUrlPagination] = useUrlState({
        page: 1,
        perPage: 25,
    })

    const { data, isFetching: isLoading } = espelhoPonto.useFetch<
        PaginatedResponse<CalculoEspelho>
    >({
        keepPreviousData: true,
        queryParams: {
            competencia,
            empresaId,
            page,
            perPage,
        } as any,
    })

    const useFilter = useFilterLocaisCentroStore()
    const rows = (data?.data ?? []).filter(useFilter)

    return (
        <ContentGuide>
            <OverlayApresentacao passos={passos} name="calculo" />
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={['Manutenção do ponto', 'Cálculo do ponto']}
                >
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SelectCompetencia
                            value={competencia}
                            onChange={setCompetencia}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    key="grid-calculo"
                    serverSide
                    columns={columns}
                    rows={rows}
                    loading={isLoading}
                    checkboxSelection
                    onSelectionModelChange={ids =>
                        setSelectedIds(ids as number[])
                    }
                    selectionModel={selectedIds}
                    totalRows={data?.meta?.total}
                    onPaginationChange={({ pageIndex, pageSize }) => {
                        setUrlPagination({
                            page: pageIndex + 1,
                            perPage: pageSize,
                        })
                    }}
                    defaultPageSize={perPage}
                    components={{
                        Toolbar: () => (
                            <GridMenuButton
                                startIcon={<UploadIcon />}
                                label={localeText.toolbarExport}
                                data-testid="botao_exportar-calculo"
                            >
                                <ExportarCsv
                                    label="Exportar todos como CSV"
                                    fileName="exportacao-calculo-ponto"
                                    mi
                                />
                                <BaixarCalculoButton
                                    competencia={competencia}
                                    formato="pdf"
                                />
                                <BaixarCalculoButton
                                    competencia={competencia}
                                    formato="zip"
                                />
                            </GridMenuButton>
                        ),
                    }}
                    onAdd={
                        <CalcularEmpresa
                            competencia={competencia}
                            funcionarioIds={selectedIds}
                        />
                    }
                    ToolbarProps={{ hideExportButton: true }}
                />
                <Outlet
                    key="outlet-calculo"
                    context={{ data: data?.data ?? [] }}
                />
            </ContentGuide.Card>
        </ContentGuide>
    )
}

export function useCalculoOutletContext() {
    return useOutletContext<{
        data: CalculoEspelho[]
    }>()
}
